import React, { useState, useEffect, useCallback } from 'react';
import {
  HiDownload,
  HiRefresh,
  HiDatabase,
  HiTrash,
  HiWifi,
  HiUser,
  HiCog,
  HiShieldCheck,
  HiUpload,
  HiCloud
} from 'react-icons/hi';
import { 
  HiCloudArrowDown,
  HiWrenchScrewdriver,
  HiSignalSlash
} from 'react-icons/hi2';
import { useFirebaseData, useFirebaseCacheManager } from '../hooks/useFirebaseData';
import { useOfflineData, useCacheManager } from '../hooks/useOfflineData';
import { useAuth } from '../contexts/AuthContext';
import { UserData } from '../types/user';
import { offlineAuthService } from '../services/offlineAuthService';
import { useFeedbackList, useAppUpdates } from '../hooks/useFeedbackData';
import { showUpdateAvailable, forceSwUpdate, isPwa } from '../serviceWorkerRegistration';

// Extended UserData interface for demo purposes
interface ExtendedUserData extends UserData {
  lastSeen?: string;
  offlineDemoAccessed?: boolean;
}

// Mock data interface for general demo
interface AppConfig {
  version: string;
  features: string[];
  lastUpdated: string;
}

// Mock API function for general caching demo
const fetchAppConfig = async (): Promise<AppConfig> => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Simulate network failure occasionally
  if (Math.random() < 0.1) {
    throw new Error('Network error - simulated failure');
  }

  return {
    version: '2.1.0',
    features: ['Offline Support', 'Real-time Sync', 'Firebase Integration', 'PWA Capabilities'],
    lastUpdated: new Date().toISOString(),
  };
};

// Debug Section Component
interface LocalStorageInfo {
  key: string;
  size: number;
  timestamp?: number;
  age?: string;
  dataLength?: number;
  preview?: string;
}

const DebugSection: React.FC = () => {
  const [localStorageInfo, setLocalStorageInfo] = useState<LocalStorageInfo[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [storageQuota, setStorageQuota] = useState<{used: number, quota: number} | null>(null);
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  
  // Get feedback and updates status for debugging
  const { feedbackList, loading: feedbackLoading, isFromCache: feedbackFromCache } = useFeedbackList();
  const { updates, loading: updatesLoading, isFromCache: updatesFromCache } = useAppUpdates();

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check for service worker updates
  useEffect(() => {
    const checkForUpdates = async () => {
      const updateAvailable = await showUpdateAvailable();
      setSwUpdateAvailable(updateAvailable);
      
      // Get service worker registration
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        setSwRegistration(registration);
      }
    };
    
    checkForUpdates();
  }, []);

  // Get storage quota information
  useEffect(() => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then((estimate) => {
        setStorageQuota({
          used: estimate.usage || 0,
          quota: estimate.quota || 0
        });
      });
    }
  }, []);

  const refreshLocalStorageInfo = useCallback(() => {
    const info: LocalStorageInfo[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          const size = new Blob([value]).size;
          let parsedData: any = null;
          let timestamp: number | undefined;
          let dataLength: number | undefined;
          
          try {
            parsedData = JSON.parse(value);
            if (parsedData && typeof parsedData === 'object') {
              timestamp = parsedData.timestamp;
              if (parsedData.data && Array.isArray(parsedData.data)) {
                dataLength = parsedData.data.length;
              }
            }
          } catch (e) {
            // Not JSON, that's okay
          }
          
          const age = timestamp ? formatAge(Date.now() - timestamp) : undefined;
          const preview = value.length > 100 ? value.substring(0, 100) + '...' : value;
          
          info.push({
            key,
            size,
            timestamp,
            age,
            dataLength,
            preview
          });
        }
      }
    }
    
    setLocalStorageInfo(info);
  }, []);

  const formatAge = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h ago`;
    if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s ago`;
    return `${seconds}s ago`;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const clearLocalStorage = () => {
    if (window.confirm('Are you sure you want to clear all localStorage? This will remove all cached data.')) {
      localStorage.clear();
      refreshLocalStorageInfo();
    }
  };

  const clearSpecificKey = (key: string) => {
    if (window.confirm(`Are you sure you want to remove "${key}" from localStorage?`)) {
      localStorage.removeItem(key);
      refreshLocalStorageInfo();
    }
  };

  const handleForceUpdate = () => {
    if (window.confirm('This will update the app to the latest version and reload the page. Continue?')) {
      forceSwUpdate();
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  const clearServiceWorkerCache = async () => {
    if (!window.confirm('Clear all service worker caches? This will force fresh downloads on next load.')) {
      return;
    }
    
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      alert('Service worker caches cleared! The page will reload.');
      window.location.reload();
    } catch (error) {
      console.error('Error clearing caches:', error);
      alert('Error clearing caches. Check console for details.');
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Load localStorage info on mount
  useEffect(() => {
    refreshLocalStorageInfo();
  }, [refreshLocalStorageInfo]);

  const cacheKeys = localStorageInfo.filter(item => 
    item.key === 'feedback-list' || item.key === 'app-updates'
  );

  return (
    <div className="card bg-base-100 shadow-lg">
      <div className="card-body">
        <h2 className="card-title text-lg">Debug Tools</h2>
        
        {/* Service Worker Status */}
        <div className="bg-base-200 p-4 rounded-lg mb-4">
          <h3 className="font-semibold text-base mb-4">Service Worker Status</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">App Type:</span>
              <div className={`badge ${isPwa() ? 'badge-success' : 'badge-ghost'}`}>
                {isPwa() ? 'PWA (Installed)' : 'Browser'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Service Worker:</span>
              <div className={`badge ${swRegistration ? 'badge-success' : 'badge-error'}`}>
                {swRegistration ? 'Active' : 'Not Available'}
              </div>
            </div>
            {swUpdateAvailable && (
              <div className="alert alert-warning">
                <span>New app version available! This might fix offline issues.</span>
                <button className="btn btn-sm" onClick={handleForceUpdate}>
                  <HiUpload className="w-4 h-4 mr-1" />
                  Update Now
                </button>
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <button 
                className="btn btn-outline btn-warning btn-sm"
                onClick={clearServiceWorkerCache}
              >
                                 <HiWrenchScrewdriver className="w-4 h-4 mr-1" />
                 Clear SW Cache
              </button>
              <button 
                className="btn btn-outline btn-sm"
                onClick={() => window.location.reload()}
              >
                <HiRefresh className="w-4 h-4 mr-1" />
                Hard Reload
              </button>
            </div>
          </div>
        </div>
        
        {/* Connection Status */}
                 <div className={`alert ${isOnline ? 'alert-success' : 'alert-warning'} mb-4`}>
           <div className="flex items-center gap-2">
             {isOnline ? <HiCloud className="w-5 h-5" /> : <HiSignalSlash className="w-5 h-5" />}
             <span>Connection Status: {isOnline ? 'Online' : 'Offline'}</span>
           </div>
          <button className="btn btn-sm btn-ghost" onClick={refreshLocalStorageInfo}>
            <HiRefresh className="w-4 h-4 mr-1" />
            Refresh Data
          </button>
        </div>

        {/* App State Summary */}
        <div className="bg-base-200 p-4 rounded-lg mb-4">
          <h3 className="font-semibold text-base mb-4">App Data Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium">Feedback:</span>
                <div className={`badge ${feedbackFromCache ? 'badge-warning' : 'badge-success'}`}>
                  {feedbackFromCache ? 'From Cache' : 'Fresh'}
                </div>
                <div className="badge badge-outline">
                  {feedbackList.length} items
                </div>
                {feedbackLoading && <div className="badge">Loading...</div>}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium">Updates:</span>
                <div className={`badge ${updatesFromCache ? 'badge-warning' : 'badge-success'}`}>
                  {updatesFromCache ? 'From Cache' : 'Fresh'}
                </div>
                <div className="badge badge-outline">
                  {updates.length} items
                </div>
                {updatesLoading && <div className="badge">Loading...</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Storage Quota */}
        {storageQuota && (
          <div className="bg-base-200 p-4 rounded-lg mb-4">
            <h3 className="font-semibold text-base mb-4">Storage Information</h3>
            <p className="text-sm text-base-content/70 mb-2">
              Used: {formatBytes(storageQuota.used)} / {formatBytes(storageQuota.quota)} 
              ({((storageQuota.used / storageQuota.quota) * 100).toFixed(2)}%)
            </p>
            <div className="w-full h-2 bg-base-300 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${(storageQuota.used / storageQuota.quota) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Cache Status */}
        <div className="bg-base-200 p-4 rounded-lg mb-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-base">Cache Status</h3>
            <button 
              className="btn btn-error btn-sm"
              onClick={clearLocalStorage}
            >
              <HiTrash className="w-4 h-4 mr-1" />
              Clear All Cache
            </button>
          </div>
          
          {cacheKeys.length === 0 ? (
            <div className="alert alert-warning">
              <span>No cache data found! This might explain why offline mode isn't working.</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cacheKeys.map((item) => (
                <div key={item.key} className="card card-compact bg-base-100 border">
                  <div className="card-body">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-medium">{item.key}</h4>
                      <button 
                        className="btn btn-xs btn-ghost"
                        onClick={() => clearSpecificKey(item.key)}
                      >
                        <HiTrash className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="text-xs text-base-content/70 space-y-1">
                      <p>Size: {formatBytes(item.size)}</p>
                      {item.dataLength && <p>Items: {item.dataLength}</p>}
                      {item.age && <p>Age: {item.age}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Full LocalStorage Debug */}
        <div className="collapse collapse-arrow bg-base-200">
          <input 
            type="checkbox" 
            checked={expandedSections.localStorage || false}
            onChange={() => toggleSection('localStorage')}
          />
          <div className="collapse-title text-base font-medium">
            <div className="flex items-center gap-2">
              <HiDatabase className="w-5 h-5" />
              <span>Full LocalStorage Debug</span>
              <div className="badge badge-outline">{localStorageInfo.length} keys</div>
            </div>
          </div>
          <div className="collapse-content">
            {localStorageInfo.length === 0 ? (
              <p className="text-base-content/70">No localStorage data found</p>
            ) : (
              <div className="space-y-4">
                {localStorageInfo.map((item, index) => (
                  <div key={item.key} className="card card-compact bg-base-100 border">
                    <div className="card-body">
                      <div className="flex justify-between items-center mb-2">
                        <code className="text-sm font-mono">{item.key}</code>
                        <button 
                          className="btn btn-xs btn-ghost"
                          onClick={() => clearSpecificKey(item.key)}
                        >
                          <HiTrash className="w-3 h-3" />
                          Remove
                        </button>
                      </div>
                      <div className="text-xs text-base-content/70 mb-2">
                        Size: {formatBytes(item.size)}
                        {item.dataLength && ` | Items: ${item.dataLength}`}
                        {item.age && ` | Age: ${item.age}`}
                      </div>
                      <div className="bg-base-300 p-2 rounded text-xs font-mono max-h-24 overflow-auto">
                        {item.preview}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const OfflineDemo: React.FC = () => {
  const { currentUser, authState, isOfflineMode } = useAuth();
  
  // Demonstrate general cache-first strategy
  const configResult = useOfflineData({
    key: 'app-config',
    fetchFn: fetchAppConfig,
    maxAge: 300, // Cache for 5 minutes
    strategy: 'cache-first',
  });

  // Demonstrate Firebase data with real user data
  const userResult = useFirebaseData<ExtendedUserData>({
    collection: 'users',
    docId: currentUser?.uid || 'demo-user',
    maxAge: 600, // Cache for 10 minutes
    enableRealtimeUpdates: true,
    fallbackToCache: true,
    enabled: !!currentUser?.uid,
  });

  // Create a demo document for testing
  const demoResult = useFirebaseData({
    collection: 'demo',
    docId: 'offline-test',
    maxAge: 300, // Cache for 5 minutes
    enableRealtimeUpdates: true,
    fallbackToCache: true,
  });

  const generalCacheManager = useCacheManager();
  const firebaseCacheManager = useFirebaseCacheManager();

  const getStatusChip = (isFromCache: boolean, isOffline: boolean, loading: boolean) => {
    if (loading) {
      return (
        <div className="badge">
          <span className="loading loading-spinner loading-xs mr-1"></span>
          Loading...
        </div>
      );
    }
    if (isOffline) {
             return (
         <div className="badge badge-warning">
           <HiSignalSlash className="w-3 h-3 mr-1" />
           Offline
         </div>
       );
    }
    if (isFromCache) {
      return (
        <div className="badge badge-info">
          <HiDatabase className="w-3 h-3 mr-1" />
          From Cache
        </div>
      );
    }
    return (
      <div className="badge badge-success">
        <HiDownload className="w-3 h-3 mr-1" />
        From Network
      </div>
    );
  };

  const handleCreateDemoData = async () => {
    if (demoResult.updateData) {
      try {
        await demoResult.updateData({
          message: 'Hello from offline demo!',
          timestamp: new Date().toISOString(),
          counter: Math.floor(Math.random() * 1000),
          features: ['offline', 'realtime', 'cached'],
        });
      } catch (error) {
        console.error('Failed to create demo data:', error);
      }
    }
  };

  const handleUpdateUserData = async () => {
    if (userResult.updateData && userResult.data) {
      try {
        await userResult.updateData({
          ...userResult.data,
          lastSeen: new Date().toISOString(),
          offlineDemoAccessed: true,
        });
      } catch (error) {
        console.error('Failed to update user data:', error);
      }
    }
  };

  const handleClearOfflineAuth = () => {
    offlineAuthService.clearAuthState();
    // Force page refresh to see the effect
    window.location.reload();
  };

  const authCacheInfo = offlineAuthService.getCacheInfo();

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-4">Offline Capabilities Check</h1>
      
      <p className="text-base-content/80 mb-6">
        This page demonstrates how the app works with different caching strategies using both 
        general data and Firebase. Try turning off your network connection to see how cached 
        data is served offline, and observe real-time updates when online.
      </p>

      {/* Connectivity Status */}
             <div className={`alert ${generalCacheManager.isOffline ? 'alert-warning' : 'alert-success'} mb-6`}>
         <div className="flex items-center gap-2">
           {generalCacheManager.isOffline ? <HiSignalSlash className="w-5 h-5" /> : <HiWifi className="w-5 h-5" />}
           <span>
             You are currently {generalCacheManager.isOffline ? 'offline' : 'online'}
             {generalCacheManager.isServiceWorkerSupported && ' - Service Worker is active'}
             {isOfflineMode && ' - Using offline authentication'}
           </span>
         </div>
       </div>

      <div className="space-y-6">
        {/* Authentication Status */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex justify-between items-center mb-4">
              <h2 className="card-title">
                <HiShieldCheck className="w-5 h-5 mr-2" />
                Offline Authentication Status
              </h2>
                             <div className={`badge ${isOfflineMode ? 'badge-warning' : 'badge-success'}`}>
                 {isOfflineMode ? <HiCloudArrowDown className="w-4 h-4 mr-1" /> : <HiShieldCheck className="w-4 h-4 mr-1" />}
                 {isOfflineMode ? 'Offline Mode' : 'Online Mode'}
               </div>
            </div>

            <div className="space-y-2">
              <p><strong>Authentication:</strong> {authState.isAuthenticated ? 'Authenticated' : 'Not authenticated'}</p>
              <p><strong>Mode:</strong> {isOfflineMode ? 'Offline (using cache)' : 'Online (using Firebase)'}</p>
              {authCacheInfo && (
                <>
                  <p><strong>Auth Cache:</strong> {authCacheInfo.exists ? 'Available' : 'Not available'}</p>
                  {authCacheInfo.exists && (
                    <>
                      <p><strong>Cache Age:</strong> {Math.floor(authCacheInfo.age / (1000 * 60))} minutes</p>
                      <p><strong>Expires In:</strong> {Math.floor(authCacheInfo.expiresIn / (1000 * 60 * 60))} hours</p>
                    </>
                  )}
                </>
              )}
            </div>

            {authCacheInfo?.exists && (
              <div className="mt-4">
                <button
                  className="btn btn-warning btn-sm"
                  onClick={handleClearOfflineAuth}
                >
                  <HiTrash className="w-4 h-4 mr-1" />
                  Clear Offline Auth Cache
                </button>
              </div>
            )}

            <div className="alert alert-info mt-4">
              <span>
                <strong>How it works:</strong> When online, authentication uses Firebase. When offline, 
                the app uses cached authentication state (valid for 7 days) so you can continue using 
                the app without being redirected to login.
              </span>
            </div>
          </div>
        </div>

        {/* Data Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* App Config Section (General Caching) */}
          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <div className="flex justify-between items-center mb-4">
                <h2 className="card-title">
                  <HiCog className="w-5 h-5 mr-2" />
                  App Config (Cache-First)
                </h2>
                {getStatusChip(configResult.isFromCache, configResult.isOffline, configResult.loading)}
              </div>
              
              {configResult.error && (
                <div className="alert alert-error mb-4">
                  <span>{configResult.error.message}</span>
                </div>
              )}

              {configResult.data && (
                <div className="space-y-2">
                  <p><strong>Version:</strong> {configResult.data.version}</p>
                  <div>
                    <strong>Features:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      {configResult.data.features.map((feature, index) => (
                        <li key={index} className="text-sm">{feature}</li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-base-content/70">
                    Last updated: {new Date(configResult.data.lastUpdated).toLocaleString()}
                  </p>
                </div>
              )}

              <div className="flex gap-2 mt-4 flex-wrap">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={configResult.refresh}
                  disabled={configResult.loading}
                >
                  <HiRefresh className="w-4 h-4 mr-1" />
                  Refresh
                </button>
                <button
                  className="btn btn-warning btn-sm"
                  onClick={configResult.clearCache}
                  disabled={configResult.loading}
                >
                  <HiTrash className="w-4 h-4 mr-1" />
                  Clear Cache
                </button>
              </div>

              {configResult.lastUpdated && (
                <p className="text-xs text-base-content/70 mt-2">
                  Hook last updated: {configResult.lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>

          {/* Firebase User Data Section */}
          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <div className="flex justify-between items-center mb-4">
                <h2 className="card-title">
                  <HiUser className="w-5 h-5 mr-2" />
                  User Profile (Firebase Real-time)
                </h2>
                {getStatusChip(userResult.isFromCache, userResult.isOffline, userResult.loading)}
              </div>

              {!authState.isAuthenticated && (
                <div className="alert alert-info mb-4">
                  <span>Please log in to see Firebase user data caching in action.</span>
                </div>
              )}

              {userResult.error && (
                <div className="alert alert-error mb-4">
                  <span>{userResult.error.message}</span>
                </div>
              )}

              {userResult.data && (
                <div className="space-y-2">
                  <p><strong>Name:</strong> {userResult.data.name} {userResult.data.surname}</p>
                  <p><strong>Email:</strong> {userResult.data.email}</p>
                  <p><strong>Country:</strong> {userResult.data.country}</p>
                  {userResult.data.lastSeen && (
                    <p className="text-xs text-base-content/70">
                      Last seen: {new Date(userResult.data.lastSeen).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-2 mt-4 flex-wrap">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={userResult.refresh}
                  disabled={userResult.loading || !authState.isAuthenticated}
                >
                  <HiRefresh className="w-4 h-4 mr-1" />
                  Refresh
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleUpdateUserData}
                  disabled={userResult.loading || !authState.isAuthenticated}
                >
                  <HiUser className="w-4 h-4 mr-1" />
                  Update Last Seen
                </button>
                <button
                  className="btn btn-warning btn-sm"
                  onClick={userResult.clearCache}
                  disabled={userResult.loading}
                >
                  <HiTrash className="w-4 h-4 mr-1" />
                  Clear Cache
                </button>
              </div>

              {userResult.lastUpdated && (
                <p className="text-xs text-base-content/70 mt-2">
                  Last updated: {userResult.lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Demo Firebase Document */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex justify-between items-center mb-4">
              <h2 className="card-title">
                Firebase Demo Document (Real-time + Cache)
              </h2>
              {getStatusChip(demoResult.isFromCache, demoResult.isOffline, demoResult.loading)}
            </div>

            {demoResult.error && (
              <div className="alert alert-error mb-4">
                <span>{demoResult.error.message}</span>
              </div>
            )}

            {demoResult.data ? (
              <div className="space-y-2">
                <p><strong>Message:</strong> {demoResult.data.message}</p>
                <p><strong>Counter:</strong> {demoResult.data.counter}</p>
                <p><strong>Features:</strong> {demoResult.data.features?.join(', ')}</p>
                <p className="text-xs text-base-content/70">
                  Created: {new Date(demoResult.data.timestamp).toLocaleString()}
                </p>
              </div>
            ) : (
              <p className="text-base-content/70">
                No demo data found. Create some to test Firebase caching!
              </p>
            )}

            <div className="flex gap-2 mt-4 flex-wrap">
              <button
                className="btn btn-primary"
                onClick={handleCreateDemoData}
                disabled={demoResult.loading}
              >
                Create/Update Demo Data
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={demoResult.refresh}
                disabled={demoResult.loading}
              >
                <HiRefresh className="w-4 h-4 mr-1" />
                Refresh
              </button>
              <button
                className="btn btn-warning btn-sm"
                onClick={demoResult.clearCache}
                disabled={demoResult.loading}
              >
                <HiTrash className="w-4 h-4 mr-1" />
                Clear Cache
              </button>
            </div>
          </div>
        </div>

        {/* Service Worker & LocalStorage Debug */}
        <DebugSection />

        {/* Cache Management */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h2 className="card-title mb-4">Cache Management & Statistics</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-base font-semibold mb-2">General Cache</h3>
                <div className="space-y-2">
                  <p><strong>Entries:</strong> {generalCacheManager.cacheInfo.keys.length}</p>
                  <p><strong>Size:</strong> {(generalCacheManager.cacheInfo.size / 1024).toFixed(1)} KB</p>
                  <p><strong>Service Worker:</strong> {generalCacheManager.isServiceWorkerSupported ? 'Active' : 'Not supported'}</p>
                </div>
                
                <div className="flex gap-2 mt-4 flex-wrap">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={generalCacheManager.refreshCacheInfo}
                  >
                    <HiRefresh className="w-4 h-4 mr-1" />
                    Refresh Info
                  </button>
                  <button
                    className="btn btn-error btn-sm"
                    onClick={generalCacheManager.clearAllCache}
                  >
                    <HiTrash className="w-4 h-4 mr-1" />
                    Clear All
                  </button>
                </div>
              </div>
              
              <div>
                <h3 className="text-base font-semibold mb-2">Firebase Cache</h3>
                <div className="space-y-2">
                  <p><strong>Entries:</strong> {firebaseCacheManager.cacheInfo.keys.length}</p>
                  <p><strong>Size:</strong> {(firebaseCacheManager.cacheInfo.size / 1024).toFixed(1)} KB</p>
                  <p><strong>Status:</strong> {firebaseCacheManager.isOffline ? 'Offline' : 'Online'}</p>
                </div>

                <div className="flex gap-2 mt-4 flex-wrap">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={firebaseCacheManager.refreshCacheInfo}
                  >
                    <HiRefresh className="w-4 h-4 mr-1" />
                    Refresh Info
                  </button>
                  <button
                    className="btn btn-error btn-sm"
                    onClick={firebaseCacheManager.clearAllCache}
                  >
                    <HiTrash className="w-4 h-4 mr-1" />
                    Clear Firebase Cache
                  </button>
                </div>
              </div>
            </div>

            <div className="divider"></div>
            
            <div className="text-sm text-base-content/70">
              <strong>Testing Tips:</strong> 
              <br />• Go offline (DevTools → Network → Offline) to test cached data loading
              <br />• Watch real-time updates when multiple tabs are open
              <br />• Clear cache to see fresh data fetching
              <br />• Firebase data includes real-time listeners with offline fallback
              <br />• Authentication works offline using cached credentials (valid for 7 days)
              <br />• Try refreshing the page while offline - you should stay logged in
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 